'use client';
import classNames from 'classnames';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TypeChildRecord } from '@/lib/api/accountManager/types';
import { TypeImage } from '@/core/generated-types';
import { ChildTab } from './components/ChildTab';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';

export interface ChildSelectorProps {
  visibleChildren: TypeChildRecord[];
  t: TypePlayKitPageTranslations;
  icons: TypeImage[];
  selectedChild: TypeChildRecord | undefined;
  setSelectedChild: (newChildUuid: string) => void;
}

export type TypeChildTabItem = {
  id: string;
  title: string;
  icon: TypeImage;
};

const ADD_CHILD_ID = 'add_child';

const ChildSelector: FC<ChildSelectorProps> = ({
  t,
  visibleChildren = [],
  icons,
  selectedChild,
  setSelectedChild,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  const items: TypeChildTabItem[] = useMemo(() => {
    const formattedVisibleChildren = visibleChildren.map((child, index) => ({
      id: child.uuid,
      title: child.name,
      icon: icons[index % icons.length],
    }));

    return [
      ...formattedVisibleChildren,
      {
        id: ADD_CHILD_ID,
        title: t.addChild,
        icon: icons[icons.length],
      },
    ];
  }, [visibleChildren, t.addChild, icons]);

  // used to toggle between justify-center and justify-left (necessary for large quantities of items)
  const checkOverflow = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      setIsOverflowing(container.scrollWidth > container.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  return (
    <div
      data-testid="child-selector"
      ref={containerRef}
      className={classNames(
        'w-full flex flex-row pt-10 flex-nowrap overflow-x-auto scroll-smooth pb-6 lg:pb-8 space-x-2 lg:space-x-6 ',
        {
          'justify-start': isOverflowing,
          'md:justify-center': !isOverflowing,
        },
      )}
    >
      {items.map((item) => (
        <ChildTab
          key={item.id}
          childTabItem={item}
          editText={t.edit}
          isSelected={selectedChild?.uuid === item.id}
          isDefault={item.id === ADD_CHILD_ID}
          setSelectedChild={setSelectedChild}
        />
      ))}
    </div>
  );
};

export default React.memo(ChildSelector);
